import React, { useState, useMemo, useCallback, useEffect } from 'react';
import FiltroEvoVendas from '../components/evo_vendas/FiltroEvoVendas';
import GraficoComparativoAnual from '../components/evo_vendas/GraficoComparativoAnual';
import CardCombustivel from '../components/evo_vendas/CardCombustivel';
import { construirFiltrosAPI } from '../utils/filtrosHelper';
import { getCards, getTotalPostos } from '../services/api';
import { processarCardsCombustivel } from '../components/evo_vendas/CardCombustivel';

const EvoVendas = ({ showFilters = true, onDataUpdate }) => {
  const [filtrosSelecionados, setFiltrosSelecionados] = useState({});
  const [filtroCruzadoData, setFiltroCruzadoData] = useState(null);
  const [loadingCards, setLoadingCards] = useState(true);
  const [valoresCards, setValoresCards] = useState({});

  // Combinar filtros normais com filtro cruzado (para os cards)
  const filtrosCombinados = useMemo(() => {
    const novosFiltros = { ...filtrosSelecionados };
    if (filtroCruzadoData) {
      novosFiltros['visao_geral.data_hora_abastecimento_sp_date'] = filtroCruzadoData;
    }
    return novosFiltros;
  }, [filtrosSelecionados, filtroCruzadoData]);

  // Converter filtros para formato da API - para os cards (com filtro cruzado)
  const filtrosAPI = useMemo(() => {
    return construirFiltrosAPI(filtrosCombinados);
  }, [filtrosCombinados]);

  // Filtros apenas normais para o gráfico (sem filtro cruzado - mantém todas as linhas visíveis)
  const filtrosGrafico = useMemo(() => {
    return construirFiltrosAPI(filtrosSelecionados);
  }, [filtrosSelecionados]);

  // Callback para aplicar filtro cruzado de período
  const handlePeriodoSelecionado = useCallback((periodo) => {
    setFiltroCruzadoData(periodo);
  }, []);

  // OTIMIZAÇÃO: buscar dados dos cards UMA vez (evita 7x getCards + 1x getVisaoGeral)
  const filtrosAPIKey = useMemo(() => JSON.stringify(filtrosAPI), [filtrosAPI]);
  useEffect(() => {
    let cancelled = false;
    const fetchCards = async () => {
      setLoadingCards(true);
      try {
        const [dadosCards, totalPostos] = await Promise.all([
          getCards(filtrosAPI),
          getTotalPostos(filtrosAPI),
        ]);
        if (cancelled) return;

        const cards = processarCardsCombustivel(dadosCards);
        const map = {};
        cards.forEach((c) => {
          map[c.categoria] = c.valor;
        });
        map.total_litros = cards.reduce((acc, c) => acc + (c.valor || 0), 0);
        map.postos = totalPostos;

        setValoresCards(map);
      } catch (e) {
        if (cancelled) return;
        setValoresCards({});
      } finally {
        if (!cancelled) setLoadingCards(false);
      }
    };

    fetchCards();
    return () => {
      cancelled = true;
    };
  }, [filtrosAPIKey]);

  return (
    <div className="bg-gray-50 pb-4">
      {/* Área de filtros - com animação de mostrar/ocultar */}
      <div
        className={`w-full mx-auto transition-all duration-300 ease-in-out overflow-hidden ${showFilters ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        style={{ padding: '0 var(--space-fluid-md)' }}
      >
        <FiltroEvoVendas onChange={setFiltrosSelecionados} />
      </div>

      {/* Conteúdo principal */}
      <div className="w-full mx-auto" style={{ padding: '0 var(--space-fluid-sm)' }}>

        {/* Badge de Filtro Cruzado Ativo - acima dos cards */}
        {filtroCruzadoData && (
          <div style={{ marginTop: 'var(--space-fluid-sm)' }}>
            <div className="bg-amber-50 border border-amber-200 rounded-xl" style={{ padding: 'var(--space-fluid-sm)' }}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-amber-600 font-medium" style={{ fontSize: 'var(--text-fluid-xs)' }}>
                  🔗 Filtros cruzados ativos (1):
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-amber-800 font-semibold" style={{ fontSize: 'var(--text-fluid-xs)' }}>
                    📅 Data: {filtroCruzadoData}
                  </span>
                  <button
                    onClick={() => setFiltroCruzadoData(null)}
                    className="text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-lg font-medium transition-colors"
                    style={{ fontSize: 'var(--text-fluid-xs)', padding: 'var(--space-fluid-xs) var(--space-fluid-sm)' }}
                    title="Remover filtro de data"
                  >
                    ✕
                  </button>
                </div>
                <button
                  onClick={() => setFiltroCruzadoData(null)}
                  className="text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-lg font-medium transition-colors"
                  style={{ fontSize: 'var(--text-fluid-xs)', padding: 'var(--space-fluid-xs) var(--space-fluid-sm)' }}
                  title="Limpar todos os filtros"
                >
                  ✕ Limpar todos
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cards de Combustível */}
        <div style={{ marginTop: 'var(--space-fluid-sm)', paddingTop: 'var(--space-fluid-sm)', paddingBottom: 'var(--space-fluid-sm)' }}>
          <div className="metrics-grid-evo">
            <CardCombustivel
              categoria="Gasolina C"
              label="Gasolina C"
              cor="bg-red-500"
              icon={
                <img src="/icons/gasolina.png" alt="Gasolina C" className="w-3 h-3" />
              }
              filtros={filtrosAPI}
              loadingExterno={loadingCards}
              valorExterno={valoresCards['Gasolina C'] ?? null}
            />
            <CardCombustivel
              categoria="Gasolina A"
              label="Gasolina A"
              cor="bg-[#039669]"
              icon={
                <img src="/icons/gasolina.png" alt="Gasolina A" className="w-3 h-3" />
              }
              filtros={filtrosAPI}
              loadingExterno={loadingCards}
              valorExterno={valoresCards['Gasolina A'] ?? null}
            />
            <CardCombustivel
              categoria="Diesel S500"
              label="Diesel S500"
              cor="bg-orange-500"
              icon={
                <img src="/icons/caminhao.png" alt="Diesel S500" className="w-3 h-3" />
              }
              filtros={filtrosAPI}
              loadingExterno={loadingCards}
              valorExterno={valoresCards['Diesel S500'] ?? null}
            />
            <CardCombustivel
              categoria="Diesel S10"
              label="Diesel S10"
              cor="bg-[#1f2937]"
              icon={
                <img src="/icons/gota.png" alt="Diesel S10" className="w-3 h-3" />
              }
              filtros={filtrosAPI}
              loadingExterno={loadingCards}
              valorExterno={valoresCards['Diesel S10'] ?? null}
            />
            <CardCombustivel
              categoria="Diesel Marítimo"
              label="Diesel Marítimo"
              cor="bg-indigo-600"
              icon={
                <img src="/icons/farol.png" alt="Diesel Marítimo" className="w-3 h-3" />
              }
              filtros={filtrosAPI}
              loadingExterno={loadingCards}
              valorExterno={valoresCards['Diesel Marítimo'] ?? null}
            />
            <CardCombustivel
              categoria="Etanol"
              label="Etanol"
              cor="bg-[#84cc16]"
              icon={
                <img src="/icons/folha.png" alt="Etanol" className="w-3 h-3" />
              }
              filtros={filtrosAPI}
              loadingExterno={loadingCards}
              valorExterno={valoresCards['Etanol'] ?? null}
            />
            <CardCombustivel
              tipo="total_litros"
              label="Total de litros"
              cor="bg-[#6366f1]"
              icon={
                <img src="/icons/calculadora.png" alt="Total de litros" className="w-3 h-3" />
              }
              filtros={filtrosAPI}
              loadingExterno={loadingCards}
              valorExterno={valoresCards.total_litros ?? null}
            />
            <CardCombustivel
              tipo="postos"
              label="Postos"
              cor="bg-purple-500"
              icon={
                <img src="/icons/postos.png" alt="Postos" className="w-3 h-3" />
              }
              filtros={filtrosAPI}
              loadingExterno={loadingCards}
              valorExterno={valoresCards.postos ?? null}
            />
          </div>
        </div>

        {/* Gráfico de Comparativo Anual */}
        {/* Em mobile, o conteúdo do gráfico (legendas + cards) pode passar de 600px.
            Então deixamos altura fluida para não "vazar" e sobrepor o footer. */}
        <div style={{ marginTop: 'var(--space-fluid-md)' }}>
          <GraficoComparativoAnual
            filtros={filtrosGrafico}
            onPeriodoSelecionado={handlePeriodoSelecionado}
            filtroCruzadoAtivo={filtroCruzadoData}
          />
        </div>
      </div>
    </div>
  );
};

export default EvoVendas;

